import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0F172A] text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+61435825379" className="flex items-center gap-2 hover:text-[#F59E0B] transition-colors" data-testid="topbar-phone">
              <Phone className="w-4 h-4" />
              <span>0435 825 379</span>
            </a>
            <a href="mailto:danny@gappro.com.au" className="flex items-center gap-2 hover:text-[#F59E0B] transition-colors" data-testid="topbar-email">
              <Mail className="w-4 h-4" />
              <span>danny@gappro.com.au</span>
            </a>
          </div>
          <p className="text-slate-400">Gold Coast's Commercial Caulking Specialists</p>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass-effect shadow-lg" : "bg-white"
        } overflow-hidden`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_gappro-qld/artifacts/pi084ay0_IMG_1430.jpeg" 
                alt="Gap Pro - Caulking & Gap Sealing Specialists" 
                className="h-24 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-[#F59E0B]"
                      : "text-[#0F172A] hover:text-[#F59E0B]"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/quote">
                <Button
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-heading font-bold uppercase tracking-wide px-6"
                  data-testid="nav-get-quote-btn"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#0F172A]" />
              ) : (
                <Menu className="w-6 h-6 text-[#0F172A]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 mobile-menu-enter" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-[#F59E0B]/10 text-[#F59E0B]"
                      : "text-[#0F172A] hover:bg-slate-100"
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/quote" className="block pt-2">
                <Button
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-heading font-bold uppercase tracking-wide"
                  data-testid="mobile-get-quote-btn"
                >
                  Get a Quote
                </Button>
              </Link>
              <div className="pt-4 border-t border-slate-200 space-y-2">
                <a href="tel:+61435825379" className="flex items-center gap-2 text-slate-600 py-2">
                  <Phone className="w-4 h-4" />
                  <span>0435 825 379</span>
                </a>
                <a href="mailto:danny@gappro.com.au" className="flex items-center gap-2 text-slate-600 py-2">
                  <Mail className="w-4 h-4" />
                  <span>danny@gappro.com.au</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};
